import type { ReportPage } from '../model/schema';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Normalize the backend's two list shapes — a bare array, or an envelope with
 * `data` plus a total under `count` / `total` / `total_count` or a
 * `last_page` — into rows and a page count (cpanel's `pageCount` memo).
 * `fallbackPage` is the page count used when the envelope reports no total.
 */
export function toReportPage<T>(
  raw: unknown,
  limit: number,
  fallbackPage: number,
  parse: (item: unknown) => T | undefined,
): ReportPage<T> {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : isRecord(raw) && Array.isArray(raw.data)
      ? (raw.data as unknown[])
      : [];
  const rows = list.map(parse).filter((row): row is T => row !== undefined);
  if (Array.isArray(raw) || !isRecord(raw)) return { rows, pageCount: 1 };

  const total =
    finiteNumber(raw.count) ?? finiteNumber(raw.total) ?? finiteNumber(raw.total_count);
  if (total !== undefined) {
    return { rows, pageCount: Math.max(1, Math.ceil(total / limit)) };
  }
  const lastPage = finiteNumber(raw.last_page);
  if (lastPage !== undefined) return { rows, pageCount: Math.max(1, lastPage) };
  return { rows, pageCount: Math.max(1, fallbackPage) };
}
